import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import config from '../../config';
import { useGlobalContext } from '../../GlobalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetAll } from '../../service/BatchService';
import { GetMonthlySummary } from '../../service/LessonPlannerService';

export default function ReportMonthlyLessonPlanners() {
  const { isLoading, setIsLoading, setAppError, setAppErrorTitle, setAppErrorMessage, setAppErrorMode, appUser } = useGlobalContext();

  const [reports, setReports] = useState([]);
  const [batchs, setBatchs] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const userId = appUser._id;

  function formatTimeWithAMPM(timeStr) {
    const [hours, minutes] = timeStr.split(":").map(Number);

    const date = new Date();
    date.setHours(hours, minutes, 0); // Set time

    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  }

  const fetchMonthlyReport = async () => {
    if (!month || !year) {
      setHasSearched(true);
      setReports([]);
      return;
    }

    try {
      setIsLoading(true);
      setHasSearched(true);
      const response = await GetMonthlySummary(month, year, userId);
      console.log("Monthly Lesson Planner Report:", response);

      // Map lessonDetails to a table-friendly format
      setReports(
        (response.summary.lessonDetails || []).map((lesson, index) => ({
          id: index + 1,
          batchName: lesson.batchName,
          trainerName: lesson.trainerName,
          topic: lesson.topic,
          lessonDate: new Date(lesson.lessonDate).toLocaleDateString('en-GB'),
          lessonTime: lesson.lessonTime,
          lessonDuration: lesson.lessonDuration,
          status: lesson.status
        }))
      );
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to load lesson planner report");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch batches
  const FetchBatches = async () => {
    setIsLoading(true);
    try {
      const response = await GetAll(1, 9999, "");
      if (response && Array.isArray(response.batchs)) {
        const activeBatchs = response.batchs.filter(batch => batch.status === "active");
        setBatchs(activeBatchs);
      } else {
        setAppError(true);
        setAppErrorMessage('No Batches Found.');
      }
    } catch (error) {
      setAppError(true);
      setAppErrorMessage('Error loading batch data');
      setAppErrorMode('error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    FetchBatches();
  }, []);

  // Compute totals
  const totalLessons = reports.length;
  const totalHours = reports.reduce((sum, r) => sum + (r.lessonDuration || 0), 0);
  const totalCompleted = reports.filter(r => r.status === 'completed').length;

  const totalMinutes = reports.reduce((sum, r) => sum + (r.lessonDuration || 0), 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const totalHoursStr = `${hours} hr ${minutes} min`;


  return (
    <>
      <Helmet>
        <title>Lesson Planner Monthly Reports | {config.appName}</title>
      </Helmet>
      <div className='container'>
        <div className='page'>
          <div className='page-heading'>
            <h1>Lesson Planner Monthly Reports</h1>
            <span>
              <Link to="/"> Dashboard </Link> / Lesson Planner Monthly Reports
            </span>
          </div>
          <div className='page-content'>
            <div className="portal">
              <div className='portal-body'>
                <form>
                  <div className='row'>
                    {/* Month Dropdown */}
                    <div className='col-lg-3 col-md-3 col-sm-6 col-12'>
                      <div className="mb-3">
                        <label htmlFor="month" className="form-label">Month</label>
                        <select
                          id="month"
                          className="form-select"
                          value={month}
                          onChange={(e) => setMonth(e.target.value)}
                        >
                          <option value="">-- Select Month --</option>
                          {[
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"
                          ].map((m, index) => (
                            <option key={index + 1} value={index + 1}>{m}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Year Input */}
                    <div className='col-lg-3 col-md-3 col-sm-6 col-12'>
                      <div className="mb-3">
                        <label htmlFor="year" className="form-label">Year</label>
                        <input
                          type="number"
                          id="year"
                          className="form-control"
                          value={year}
                          onChange={(e) => setYear(e.target.value)}
                          placeholder="Enter Year"
                        />
                      </div>
                    </div>

                    {/* Search Button */}
                    <div className='col-lg-3 col-md-3 col-sm-6 col-12' style={{ marginTop: '30px' }}>
                      <div className="d-flex align-items-end mb-3">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          onClick={fetchMonthlyReport}
                        >
                          <FontAwesomeIcon icon="fa-solid fa-search" className="me-2" />
                          Search
                        </button>
                      </div>
                    </div>
                  </div>
                </form>

                {/* ...existing table code */}
                {hasSearched && (
                  reports.length > 0 ? (
                    <>
                      <div className='table-content'>
                        <div className="mobile-scroll">
                          <table className='table table-bordered table-condensed w-100'>
                            <thead>
                              <tr>
                                <th>Batch</th>
                                <th>Trainer</th>
                                <th>Topic</th>
                                <th>Lesson Date</th>
                                <th>Lesson Time</th>
                                <th>Lesson Duration (mins)</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reports.map(r => (
                                <tr key={r.id}>
                                  <td>{r.batchName}</td>
                                  <td>{r.trainerName}</td>
                                  <td>{r.topic}</td>
                                  <td>{r.lessonDate || "-"}</td>
                                  <td>{formatTimeWithAMPM(r.lessonTime)}</td>
                                  <td className="fw-bold text-end">{r.lessonDuration}</td>
                                  <td className={r.status?.toLowerCase() === 'completed' ? "text-success fw-bold" : "text-danger fw-bold"}>
                                    {r.status}
                                  </td>

                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Fancy Summary Section */}
                      <div className="summary mt-4 p-3 border rounded bg-light">
                        <h5 className="mb-3">Summary</h5>
                        <div className="row text-center">
                          <div className="col-md-4 col-sm-12 mb-2">
                            <div className="p-3 border rounded bg-white shadow-sm">
                              <h6>Total Lesson Plans</h6>
                              <p className="fw-bold fs-4">{totalLessons}</p>
                            </div>
                          </div>
                          <div className="col-md-4 col-sm-12 mb-2">
                            <div className="p-3 border rounded bg-white shadow-sm">
                              <h6>Total Hours</h6>
                              <p className="fw-bold fs-4">{totalHoursStr}</p>
                            </div>
                          </div>
                          <div className="col-md-4 col-sm-12 mb-2">
                            <div className="p-3 border rounded bg-white shadow-sm">
                              <h6>Completed Lessons</h6>
                              <p className="fw-bold fs-4 text-success">{totalCompleted}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="alert alert-warning mt-3">No record found</div>
                  )
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
